import { createFileRoute, useNavigate, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Check, X, Eye, EyeOff, Loader2, ShieldCheck, Lock, Sparkles, CreditCard, CheckCircle2 } from "lucide-react";
import { loadStripe, type Stripe as StripeJs } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { getOrCreateDeviceId, parseDeviceInfo } from "@/lib/device-id";
import { registerCurrentDevice } from "@/lib/device.functions";
import { checkEmailExists } from "@/lib/auth-check.functions";
import { createSubscription, getStripePublishableKey } from "@/lib/subscription.functions";

import { PLANS } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { BrandLogo } from "@/components/BrandLogo";
import { TurnstileGate, type TurnstileGateHandle, captchaRequiredMessage } from "@/components/TurnstileGate";
import { LegalDialog } from "@/components/LegalDialog";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { GoogleAuthButton, GoogleDivider } from "@/components/GoogleAuthButton";
import { BaridiMobPaymentCard } from "@/components/BaridiMobPaymentCard";
import { PayPalSubscribeButton } from "@/components/PayPalSubscribeButton";
import baridimobLogo from "@/assets/baridimob.png.asset.json";

const PAID_PLANS = new Set(["starter", "pro", "unlimited"]);
type PaidPlanId = "starter" | "pro" | "unlimited";



export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in to Lumvio — Freelancer & Client Portal" },
      {
        name: "description",
        content:
          "Sign in to your Lumvio account or create a new workspace to manage freelance projects, share files with clients, and get paid securely through payment protection.",
      },
      { property: "og:title", content: "Sign in to Lumvio" },
      {
        property: "og:description",
        content: "Access your Lumvio workspace to manage projects, chat with clients, and receive payment protection payments.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://lumvio.co/auth" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://lumvio.co/auth" }],
  }),
  component: AuthPage,
});


// Shared flag so the auto-redirect stays out of the way while the sign-in
// form is running its password → email-OTP two-step. signInWithPassword
// briefly creates a session before we signOut and send the OTP; without
// this, the redirect effect fires and the OTP step never renders.
let otpFlowInProgress = false;
export function setOtpFlowInProgress(v: boolean) { otpFlowInProgress = v; }

function AuthPage() {
  const { t, i18n } = useTranslation();
  const { user, loading, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const isRtl = i18n.dir() === "rtl";
  const search = useSearch({ from: "/auth" }) as { tab?: "signin" | "signup"; role?: "freelancer" | "client"; email?: string; plan?: string };
  const defaultTab = search.tab === "signup" ? "signup" : "signin";
  const paidPlan: PaidPlanId | null = search.plan && PAID_PLANS.has(search.plan) ? (search.plan as PaidPlanId) : null;

  // Persist a paid plan requested on the pricing page so we can resume
  // /checkout/$plan after freelancer sign-in/sign-up completes.
  useEffect(() => {
    if (search.plan && typeof window !== "undefined") {
      try { window.localStorage.setItem("pending_plan", search.plan); } catch {}
    }
  }, [search.plan]);

  useEffect(() => {
    if (loading || !user || otpFlowInProgress) return;
    // If the user signed up with Google as a specific role, apply it now.
    // The DB trigger defaults role='client' when no metadata is present.
    (async () => {
      let pendingRole: string | null = null;
      try { pendingRole = window.localStorage.getItem("pending_signup_role"); } catch {}
      if (pendingRole === "freelancer" || pendingRole === "client") {
        // The profile row is created by a DB trigger after OAuth signup and can
        // lag the first session by a moment — retry until it exists.
        let applied = false;
        for (let attempt = 0; attempt < 8 && !applied; attempt++) {
          try {
            const { error: rpcErr } = await supabase.rpc("set_signup_role", { _role: pendingRole });
            if (!rpcErr) {
              applied = true;
            } else if (rpcErr.message?.includes("Profile not found")) {
              await new Promise((r) => setTimeout(r, 800));
            } else {
              break; // definitive failure (e.g. role locked) — stop retrying
            }
          } catch {
            await new Promise((r) => setTimeout(r, 800));
          }
        }
        if (applied) {
          try { window.localStorage.removeItem("pending_signup_role"); } catch {}
          await refreshProfile();
        }
        // If not applied, keep pending_signup_role so the next page load retries.
      } else {
        // "Continue with Google" was clicked from the Sign in tab. Google OAuth
        // auto-creates an account for unknown emails — block that: sign the
        // brand-new account out and ask the user to sign up (and pick a role).
        const isGoogle =
          user.app_metadata?.provider === "google" ||
          (user.identities ?? []).some((i) => i.provider === "google");
        const createdAt = Date.parse(user.created_at ?? "");
        const isNew = Number.isFinite(createdAt) && Date.now() - createdAt < 3 * 60 * 1000;
        if (isGoogle && isNew) {
          try { window.localStorage.setItem("oauth_no_account_email", user.email ?? ""); } catch {}
          try { await supabase.auth.signOut(); } catch {}
          return;
        }
      }
      let pending: string | null = null;
      try { pending = window.localStorage.getItem("pending_plan"); } catch {}
      const paid = pending && ["starter", "pro", "unlimited"].includes(pending);
      if (paid) {
        try { window.localStorage.removeItem("pending_plan"); } catch {}
        navigate({ to: "/checkout/$plan", params: { plan: pending! } });
        return;
      }
      navigate({ to: "/dashboard" });
    })();
  }, [user, loading, navigate, otpFlowInProgress]);


  return (
    <div className="auth-page relative min-h-screen w-full overflow-hidden bg-background text-foreground">
      {/* Ambient brand aurora */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(1000px_600px_at_15%_10%,color-mix(in_oklab,var(--primary)_22%,transparent),transparent_60%),radial-gradient(900px_500px_at_85%_90%,color-mix(in_oklab,var(--primary)_18%,transparent),transparent_60%)]" />
        {/* floating soft bubbles */}
        <div className="absolute top-[8%] left-[10%] h-72 w-72 rounded-full bg-primary/10 blur-3xl auth-aurora" />
        <div className="absolute bottom-[6%] right-[8%] h-96 w-96 rounded-full bg-primary/10 blur-[120px] auth-aurora" style={{ animationDelay: "-8s" }} />
        <div className="absolute top-[40%] right-[20%] h-40 w-40 rounded-full bg-primary/15 blur-2xl auth-aurora" style={{ animationDelay: "-14s" }} />
        {/* subtle dot grid */}
        <div className="absolute inset-0 opacity-[0.05] dark:opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)", backgroundSize: "24px 24px" }} />
      </div>

      {/* top bar */}
      <div className="relative z-10 flex items-center justify-between px-5 py-5 sm:px-10">
        <Link to="/" aria-label={t("auth.backHome")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <BrandLogo size="sm" />
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <LanguageSwitcher variant="outline" />
        </div>
      </div>

      {/* main content — narrow bubble by default, two-column when a paid plan is selected */}
      <main className="relative z-10 flex items-start justify-center px-4 pb-14 pt-4 sm:px-6">
        <div className={`relative w-full ${paidPlan ? "max-w-5xl" : "max-w-md"}`}>
          {!paidPlan && (
            <>
              <div aria-hidden className="pointer-events-none absolute -inset-6 rounded-[36px] bg-primary/25 blur-2xl opacity-60" />
              <div aria-hidden className="pointer-events-none absolute -inset-px rounded-[32px] bg-gradient-to-b from-primary/40 via-primary/10 to-transparent" />
            </>
          )}

          <div className={paidPlan ? "grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] items-start" : ""}>
            {paidPlan && <PlanSummaryCard plan={paidPlan} />}

            <div className="relative rounded-[30px] border border-border/60 bg-card/70 backdrop-blur-2xl shadow-[0_30px_80px_-30px_color-mix(in_oklab,var(--primary)_45%,transparent)] p-6 sm:p-8">
              {/* highlight sheen */}
              <div aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent" />

              <div className="flex flex-col items-center text-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-primary">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
                  {t("auth.badge")}
                </span>
                <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight">
                  {t("auth.headline")}
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("auth.welcomeSubtitle")}
                </p>
                <Link to="/" className="mt-3 text-xs text-muted-foreground/80 hover:text-foreground transition-colors">
                  {isRtl ? "→" : "←"} {t("auth.backHome")}
                </Link>
              </div>

              <Tabs defaultValue={defaultTab} className="mt-7">
                <TabsList className="grid w-full grid-cols-2 rounded-full bg-muted/60 border border-border/60 p-1 h-11">
                  <TabsTrigger value="signin" className="rounded-full h-9 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-[0_10px_30px_-10px_color-mix(in_oklab,var(--primary)_70%,transparent)] transition-all">
                    {t("common.signIn")}
                  </TabsTrigger>
                  <TabsTrigger value="signup" className="rounded-full h-9 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-[0_10px_30px_-10px_color-mix(in_oklab,var(--primary)_70%,transparent)] transition-all">
                    {t("common.signUp")}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="signin"><SignInForm /></TabsContent>
                <TabsContent value="signup">
                  {paidPlan ? (
                    <PaidSignUpForm plan={paidPlan} initialEmail={search.email} />
                  ) : (
                    <SignUpForm
                      initialRole={search.role}
                      initialEmail={search.email}
                    />
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <p className="mt-5 text-center text-[11px] text-muted-foreground/80 px-4">
            {t("auth.trustLine")}
          </p>
        </div>
      </main>

    </div>
  );
}

function SignInForm() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<"credentials" | "otp">("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showSiPw, setShowSiPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [shakeKey, setShakeKey] = useState(0);
  const [emailNotFound, setEmailNotFound] = useState(false);
  const [notFoundEmail, setNotFoundEmail] = useState("");
  const captchaRef = useRef<TurnstileGateHandle>(null);
  const checkEmail = useServerFn(checkEmailExists);


  // OTP state
  const [otpCode, setOtpCode] = useState("");

  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  // A Google sign-in with no existing account is blocked in AuthPage and lands
  // back here signed out — surface the same "no account, go to sign up" alert.
  useEffect(() => {
    try {
      const flagged = window.localStorage.getItem("oauth_no_account_email");
      if (flagged !== null) {
        window.localStorage.removeItem("oauth_no_account_email");
        setNotFoundEmail(flagged);
        setEmailNotFound(true);
      }
    } catch {}
  }, []);

  async function sendOtp(): Promise<boolean> {
    const captchaToken = (await captchaRef.current?.ensureToken()) ?? undefined;
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
      toast.error(captchaRequiredMessage(t));
      return false;
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/dashboard`,
        captchaToken,
      },
    });
    captchaRef.current?.reset();
    if (error) {
      toast.error(error.message);
      return false;
    }
    setResendIn(45);
    return true;
  }

  async function onSubmitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setPwError(null);
    setEmailNotFound(false);

    // First check whether this email has an account before asking for a password.
    try {
      const { exists } = await checkEmail({ data: { email } });
      if (!exists) {
        setBusy(false);
        setNotFoundEmail(email);
        setEmailNotFound(true);
        setShakeKey((k) => k + 1);
        return;
      }
    } catch (err) {
      // If the check fails, fall through to Supabase's own error so the user
      // isn't blocked by a transient server problem.
      console.warn("email existence check failed", err);
    }



    const captchaToken = (await captchaRef.current?.ensureToken()) ?? undefined;
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
      setBusy(false);
      toast.error(captchaRequiredMessage(t));
      return;
    }
    // Block AuthPage's auto-redirect during the brief window where the

    // password sign-in creates a session before we sign out.
    setOtpFlowInProgress(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password, options: { captchaToken } });
    captchaRef.current?.reset();
    if (error) {
      setOtpFlowInProgress(false);
      setBusy(false);
      const msg = /invalid|credential|password/i.test(error.message)
        ? "Wrong password. Please try again."
        : error.message;
      setPwError(msg);
      setShakeKey((k) => k + 1);
      toast.error(msg);
      return;
    }
    // Step 2: sign out immediately, then send OTP as second factor
    await supabase.auth.signOut();
    const sent = await sendOtp();
    setBusy(false);
    if (sent) {
      setStep("otp");
      toast.success("We sent an 8-digit code to your email");
    } else {
      setOtpFlowInProgress(false);
    }
  }

  async function onVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (otpCode.length !== 8) return toast.error("Enter the 8-digit code");
    setBusy(true);
    // Auth logs show this project's email provider currently issues the
    // challenge as a recovery token. Verify that exact type first, then keep
    // normal email OTP compatibility for providers configured conventionally.
    let { data: verifiedAuth, error } = await supabase.auth.verifyOtp({
      email,
      token: otpCode,
      type: "recovery",
    });
    if (error) {
      const retry = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: "email",
      });
      verifiedAuth = retry.data;
      error = retry.error;
    }
    if (error) {
      setBusy(false);
      return toast.error(
        error.message.includes("expired") || error.message.includes("invalid")
          ? "Code expired or invalid. Tap Resend to get a new one."
          : error.message,
      );
    }
    if (!verifiedAuth.session) {
      setBusy(false);
      return toast.error("Sign-in could not be completed. Please request a new code.");
    }
    // STRICT device hard-lock: register/verify this device server-side.
    try {
      const deviceId = getOrCreateDeviceId();
      const info = parseDeviceInfo();
      const res = await registerCurrentDevice({
        data: {
          deviceId,
          deviceName: info.name,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          location: null,
        },
      });
      if (res.status === "banned") {
        await supabase.auth.signOut();
        setOtpFlowInProgress(false);
        window.location.href = "/banned";
        return;
      }
      if (res.status === "device_limit_reached") {
        // Do NOT sign out here — /device-limit signs out after reading the
        // device list so the user can see what's locked.
        setOtpFlowInProgress(false);
        window.location.href = "/device-limit";
        return;
      }
    } catch (e) {
      // Non-fatal: allow sign-in through but log to console.
      console.warn("device registration failed", e);
    }
    setOtpFlowInProgress(false);
    toast.success(t("auth.toastSignedIn"));
    // verifyOtp has already persisted the returned session. Replace the page
    // immediately so the protected dashboard gate reads that session without
    // an additional auth round-trip or requiring a manual refresh.
    let pending: string | null = null;
    try { pending = window.localStorage.getItem("pending_plan"); } catch {}
    if (pending && ["starter", "pro", "unlimited"].includes(pending)) {
      try { window.localStorage.removeItem("pending_plan"); } catch {}
      window.location.replace(`/checkout/${pending}`);
      return;
    }
    window.location.replace("/dashboard");
  }

  async function onResend() {
    if (resendIn > 0 || busy) return;
    setBusy(true);
    await sendOtp();
    setBusy(false);
  }

  if (step === "otp") {
    return (
      <form onSubmit={onVerifyOtp} className="mt-4 space-y-4">
        <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
          For your security, we sent an 8-digit code to{" "}
          <span className="font-medium text-foreground">{email}</span>. Enter it below to finish signing in.
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="otp-code">Verification code</Label>
          <Input
            id="otp-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{8}"
            maxLength={8}
            required
            autoFocus
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
            className="text-center text-lg tracking-[0.5em] font-semibold"
            placeholder="••••••••"
          />
        </div>
        {/* Keep the captcha mounted on this step too — resending the code
            needs a fresh token, otherwise Supabase rejects it. */}
        <TurnstileGate ref={captchaRef} action="signin-otp" />


        <Button type="submit" className="w-full" disabled={busy || otpCode.length !== 8}>
          {busy ? (
            <>
              <Loader2 className="me-2 h-4 w-4 animate-spin" />
              Verifying…
            </>
          ) : (
            "Verify & sign in"
          )}
        </Button>
        <div className="flex items-center justify-between text-xs">
          <button
            type="button"
            onClick={() => {
              setStep("credentials");
              setOtpFlowInProgress(false);
              setOtpCode("");
              setPassword("");
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            ← Back
          </button>
          <button
            type="button"
            disabled={resendIn > 0 || busy}
            onClick={onResend}
            className="font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline"
          >
            {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <>
      <div className="mt-6">
        <GoogleAuthButton />
        <GoogleDivider />
      </div>
      <form onSubmit={onSubmitCredentials} className="space-y-5">

        <div className="space-y-1.5">
          <Label htmlFor="si-email" className="text-xs font-medium text-foreground/80">{t("common.email")}</Label>
          <Input
            id="si-email"
            type="email"
            required
            dir="ltr"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (emailNotFound) setEmailNotFound(false); }}
            placeholder="you@example.com"
            className="h-12 rounded-xl bg-background/60 border-border/70 focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/60"
          />
        </div>
        <div key={shakeKey} className={`space-y-1.5 ${pwError || emailNotFound ? "shake" : ""}`}>


          <div className="flex items-center justify-between">
            <Label htmlFor="si-password" className="text-xs font-medium text-foreground/80">{t("common.password")}</Label>
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              className="text-xs font-medium text-primary hover:underline underline-offset-4"
            >
              {t("auth.forgotPassword")}
            </button>
          </div>
          <div className="relative" dir="ltr">
            <Input
              id="si-password"
              type={showSiPw ? "text" : "password"}
              required
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (pwError) setPwError(null); }}
              aria-invalid={!!pwError}
              placeholder="••••••••"
              className={`h-12 rounded-xl bg-background/60 border-border/70 focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/60 ${pwError ? "!border-destructive/80 !ring-destructive/40 focus-visible:!ring-destructive/50 focus-visible:!border-destructive/80" : ""} pe-10`}
            />
            <button
              type="button"
              aria-label={showSiPw ? t("auth.hidePassword") : t("auth.showPassword")}
              onClick={() => setShowSiPw((v) => !v)}
              className={`absolute top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground right-2`}
            >
              {showSiPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {pwError && (
            <p className="flex items-center gap-1.5 text-xs text-destructive mt-1">
              <X className="h-3.5 w-3.5" /> {pwError}
            </p>
          )}
        </div>
        {emailNotFound && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <p className="mb-2">
              {t("auth.noAccountWithThisEmail", { email: notFoundEmail })}
            </p>
            <Link
              to="/auth"
              search={{ tab: "signup", email: notFoundEmail }}
              className="inline-flex items-center font-medium text-primary hover:underline underline-offset-4"
            >
              {t("auth.goSignUp")} →
            </Link>
          </div>
        )}
        <TurnstileGate ref={captchaRef} action="signin" />

        <Button
          type="submit"
          disabled={busy}
          className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_16px_40px_-14px_color-mix(in_oklab,var(--primary)_75%,transparent)] transition-all hover:scale-[1.01]"
        >
          {busy ? (
            <>
              <Loader2 className="me-2 h-4 w-4 animate-spin" />
              {t("auth.signingIn")}
            </>
          ) : (
            <>{t("common.signIn")}</>
          )}
        </Button>
        <p className="text-center text-[11px] text-muted-foreground">
          {t("auth.otpNote")}
        </p>
      </form>
      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} defaultEmail={email} />
    </>
  );
}

// ---------- Password rules ----------
const RULES = [
  { key: "len", labelKey: "auth.passwordRules.len", test: (p: string) => p.length >= 8 },
  { key: "upper", labelKey: "auth.passwordRules.upper", test: (p: string) => /[A-Z]/.test(p) },
  { key: "lower", labelKey: "auth.passwordRules.lower", test: (p: string) => /[a-z]/.test(p) },
  { key: "num", labelKey: "auth.passwordRules.num", test: (p: string) => /\d/.test(p) },
  { key: "sym", labelKey: "auth.passwordRules.sym", test: (p: string) => /[^A-Za-z0-9]/.test(p) },
] as const;

function passwordScore(p: string) {
  return RULES.reduce((n, r) => n + (r.test(p) ? 1 : 0), 0);
}

function StrengthBar({ score }: { score: number }) {
  const { t } = useTranslation();
  const pct = (score / RULES.length) * 100;
  const color =
    score <= 2 ? "bg-destructive" : score <= 3 ? "bg-yellow-500" : score <= 4 ? "bg-blue-500" : "bg-emerald-500";
  const label = score <= 2 ? t("auth.strength.weak") : score <= 3 ? t("auth.strength.fair") : score <= 4 ? t("auth.strength.good") : t("auth.strength.strong");
  return (
    <div className="space-y-1">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={`h-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[11px] text-muted-foreground">{t("auth.strength.label")} <span className="font-medium">{label}</span></div>
    </div>
  );
}

function SignUpForm({ initialRole, initialEmail, lockRole, planContext }: { initialRole?: "freelancer" | "client"; initialEmail?: string; lockRole?: boolean; planContext?: string }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState(initialEmail ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<"freelancer" | "client">(initialRole ?? "freelancer");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [legalOpen, setLegalOpen] = useState<"terms" | "privacy" | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [existingEmail, setExistingEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const captchaRef = useRef<TurnstileGateHandle>(null);

  const score = useMemo(() => passwordScore(password), [password]);
  const allRulesMet = score === RULES.length;
  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = allRulesMet && passwordsMatch && accepted && !!fullName && !!email;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allRulesMet) {
      toast.error(t("auth.passwordNotMet"));
      return;
    }
    if (!passwordsMatch) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }
    if (!accepted) {
      toast.error(t("auth.acceptTermsError"));
      return;
    }
    setBusy(true);
    const captchaToken = (await captchaRef.current?.ensureToken()) ?? undefined;
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
      setBusy(false);
      toast.error(captchaRequiredMessage(t));
      return;
    }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: { full_name: fullName, role },
        captchaToken,
      },
    });
    captchaRef.current?.reset();
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    // Supabase returns a user with empty identities when the email already exists.
    // Detect that and prompt the user to sign in instead of showing a confirmation dialog.
    if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
      setExistingEmail(email);
      setSentTo(null);
      return;
    }
    setExistingEmail(null);
    // If confirm-email is ON, Supabase returns a user with no session.
    // Make sure we don't keep any partial session and show the confirmation box.
    if (!data.session) {
      try { await supabase.auth.signOut(); } catch {}
    }
    try {
      const { capturePostHog, PHEvents } = await import("@/lib/posthog");
      capturePostHog(PHEvents.SignupCompleted, { role, has_session: Boolean(data.session) });
    } catch { /* noop */ }
    setSentTo(email);
  }

  async function resendConfirmation() {
    if (!sentTo) return;
    setResending(true);
    const captchaToken = (await captchaRef.current?.ensureToken()) ?? undefined;
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: sentTo,
      options: { emailRedirectTo: `${window.location.origin}/dashboard`, captchaToken },
    });
    captchaRef.current?.reset();
    setResending(false);
    if (error) toast.error(error.message);
    else toast.success(t("auth.confirmResent") ?? "Confirmation email sent again.");
  }


  return (
    <>
    <Dialog open={!!sentTo} onOpenChange={(o) => { if (!o) setSentTo(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>
          </div>
          <DialogTitle className="text-center">Check your email</DialogTitle>
          <DialogDescription className="text-center">
            We sent a confirmation link to <span className="font-medium text-foreground">{sentTo}</span>.
            Click the link in the email to activate your account, then come back here to sign in.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-border/60 bg-muted/40 p-3 text-xs text-muted-foreground">
          Didn't get it? Check your spam folder, or resend below. Links expire after 24 hours.
        </div>
        <DialogFooter className="sm:justify-between gap-2">
          <Button type="button" variant="outline" onClick={resendConfirmation} disabled={resending}>
            {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resend email"}
          </Button>
          <Button type="button" onClick={() => setSentTo(null)}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {existingEmail && (
      <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
        <p className="font-medium text-foreground">{t("auth.emailExists")}</p>
        <p className="mt-1 text-muted-foreground">
          {existingEmail}{" "}
          <Link
            to="/auth"
            search={{ tab: "signin" }}
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
          >
            {t("auth.goSignIn")}
          </Link>
        </p>
      </div>
    )}
    <div className="mt-4">
      <GoogleAuthButton intendedRole={role} />
      <GoogleDivider />
    </div>
    <form onSubmit={onSubmit} className="space-y-4">



      <div className="space-y-1.5">
        <Label htmlFor="su-name">{t("common.fullName")}</Label>
        <Input id="su-name" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="su-email">{t("common.email")}</Label>
        <Input id="su-email" type="email" required dir="ltr" value={email} placeholder="you@example.com" onChange={(e) => { setEmail(e.target.value); if (existingEmail) setExistingEmail(null); }} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="su-password">{t("common.password")}</Label>
        <div className="relative" dir="ltr">
          <Input
            id="su-password"
            type={showPw ? "text" : "password"}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={"pe-10"}
          />
          <button
            type="button"
            aria-label={showPw ? t("auth.hidePassword") : t("auth.showPassword")}
            onClick={() => setShowPw((v) => !v)}
            className={`absolute top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground right-2`}
          >
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {password.length > 0 && <StrengthBar score={score} />}
        <ul className="mt-1 grid grid-cols-1 gap-0.5 text-[11px]">
          {RULES.map((r) => {
            const ok = r.test(password);
            return (
              <li
                key={r.key}
                className={`flex items-center gap-1.5 ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}
              >
                {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                {t(r.labelKey)}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="su-confirm">{t("auth.confirmPassword")}</Label>
        <div className="relative" dir="ltr">
          <Input
            id="su-confirm"
            type={showConfirm ? "text" : "password"}
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            className={`pe-10 ${
              confirm.length === 0
                ? ""
                : passwordsMatch
                  ? "border-emerald-500/60 focus-visible:ring-emerald-500/40"
                  : "border-destructive/60 focus-visible:ring-destructive/40"
            }`}
          />
          <button
            type="button"
            aria-label={showConfirm ? t("auth.hidePassword") : t("auth.showPassword")}
            onClick={() => setShowConfirm((v) => !v)}
            className={`absolute top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground right-2`}
          >
            {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {confirm.length > 0 && !passwordsMatch && (
          <p className="text-[11px] text-destructive">{t("auth.passwordMismatch")}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>{t("auth.role")}</Label>
        {lockRole ? (
          <div className="rounded-lg border border-primary/40 bg-primary/10 p-3 text-xs">
            <div className="font-medium text-foreground">
              {t("auth.freelancer")}
              {planContext ? (
                <span className="ms-1.5 rounded-full bg-primary/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-primary">
                  {planContext}
                </span>
              ) : null}
            </div>
            <div className="mt-0.5 text-muted-foreground">
              Paid plans are for freelancer accounts. Client accounts are always free.
            </div>
          </div>
        ) : (
          <RadioGroup value={role} onValueChange={(v) => setRole(v as "freelancer" | "client")} className="grid grid-cols-2 gap-2">
            <label className={`flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm ${role === "freelancer" ? "border-primary ring-1 ring-primary" : "border-border"}`}>
              <RadioGroupItem value="freelancer" />
              <span>{t("auth.freelancer")}</span>
            </label>
            <label className={`flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm ${role === "client" ? "border-primary ring-1 ring-primary" : "border-border"}`}>
              <RadioGroupItem value="client" />
              <span>{t("auth.client")}</span>
            </label>
          </RadioGroup>
        )}
      </div>


      <label className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3 text-xs">
        <Checkbox
          checked={accepted}
          onCheckedChange={(v) => setAccepted(v === true)}
          className="mt-0.5"
          id="su-terms"
        />
        <span className="text-muted-foreground">
          {t("auth.termsPrefix")}{" "}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setLegalOpen("terms"); }}
            className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
          >
            {t("auth.terms")}
          </button>{" "}
          {t("auth.and")}{" "}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setLegalOpen("privacy"); }}
            className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
          >
            {t("auth.privacy")}
          </button>
          .
        </span>
      </label>
      <LegalDialog
        open={legalOpen !== null}
        onOpenChange={(o) => !o && setLegalOpen(null)}
        kind={legalOpen ?? "terms"}
      />

      <TurnstileGate ref={captchaRef} action="signup" />
      <Button type="submit" className="w-full" disabled={busy || !canSubmit}>
        {busy ? (
          <>
            <Loader2 className="me-2 h-4 w-4 animate-spin" />
            {t("common.creating")}
          </>
        ) : (
          t("common.signUp")
        )}
      </Button>
    </form>
    </>
  );
}


function ForgotPasswordDialog({
  open,
  onOpenChange,
  defaultEmail,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  defaultEmail?: string;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const captchaRef = useRef<TurnstileGateHandle>(null);

  useEffect(() => {
    if (open) {
      setEmail(defaultEmail ?? "");
      setSent(false);
    }
  }, [open, defaultEmail]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const captchaToken = (await captchaRef.current?.ensureToken()) ?? undefined;
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
      setBusy(false);
      toast.error(captchaRequiredMessage(t));
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
      captchaToken,
    });
    captchaRef.current?.reset();
    setBusy(false);
    if (error) {
      toast.error(error.message);
    } else {
      setSent(true);
      toast.success(t("auth.resetSentToast"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("auth.resetTitle")}</DialogTitle>
          <DialogDescription>
            {t("auth.resetDescription")}
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="space-y-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
            <div className="flex items-center gap-2 font-medium text-emerald-600 dark:text-emerald-400">
              <Check className="h-4 w-4" /> {t("auth.checkInbox")}
            </div>
            <p className="text-muted-foreground">
              {t("auth.resetSentPrefix")} <span className="font-medium text-foreground">{email}</span>. {t("auth.resetSentSuffix")}
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="fp-email">{t("common.email")}</Label>
              <Input
                id="fp-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <TurnstileGate ref={captchaRef} action="reset" />
            <DialogFooter className="mt-2 gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={busy || !email}>
                {busy ? (
                  <>
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    {t("auth.sending")}
                  </>
                ) : (
                  t("auth.sendResetLink")
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Paid signup: plan summary + on-page Stripe PaymentElement
// ============================================================

function PlanSummaryCard({ plan }: { plan: PaidPlanId }) {
  const def = PLANS.find((p) => p.id === plan);
  if (!def) return null;
  const dollars = Math.floor(def.price);
  const cents = Math.round((def.price - dollars) * 100).toString().padStart(2, "0");
  return (
    <aside className="lg:sticky lg:top-6">
      <div className="relative rounded-[30px] p-[1.5px] bg-[conic-gradient(from_140deg,color-mix(in_oklab,var(--primary)_60%,transparent),transparent_35%,color-mix(in_oklab,var(--primary)_40%,transparent)_65%,transparent)] shadow-[0_40px_120px_-40px_color-mix(in_oklab,var(--primary)_50%,transparent)]">
        <div className="relative rounded-[28px] border border-border/50 bg-gradient-to-b from-card/90 to-card/60 backdrop-blur-2xl p-6 sm:p-8 overflow-hidden">
          {/* Ambient aurora */}
          <div aria-hidden className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-primary/10 blur-3xl auth-aurora" />
          <div aria-hidden className="pointer-events-none absolute -bottom-28 -left-16 h-64 w-64 rounded-full bg-[#7c3aed]/25 blur-3xl auth-aurora" style={{ animationDelay: "-9s" }} />
          <div aria-hidden className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent" />
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)", backgroundSize: "22px 22px" }} />

          <div className="relative">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-primary">
                <Sparkles className="h-3 w-3" /> Your plan
              </span>
              {def.highlight && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-primary to-primary/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground shadow-[0_10px_30px_-10px_color-mix(in_oklab,var(--primary)_70%,transparent)]">
                  Most popular
                </span>
              )}
            </div>

            <div className="mt-4 flex items-center gap-2">
              <span className="text-2xl font-semibold tracking-tight">Lumvio {def.name}</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{def.tagline}</p>

            <div className="mt-6 flex items-end gap-1">
              <span className="text-6xl font-semibold leading-none tracking-tight tabular-nums bg-gradient-to-b from-foreground via-foreground to-foreground/50 bg-clip-text text-transparent">
                ${dollars}
              </span>
              <span className="text-2xl font-semibold leading-none tracking-tight tabular-nums text-foreground/70">.{cents}</span>
              <span className="mb-1 ms-1 text-sm text-muted-foreground">/ month</span>
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground/80">USD · Billed monthly · Cancel anytime</div>

            <div className="my-6 h-px w-full bg-gradient-to-r from-transparent via-border to-transparent" />

            <ul className="space-y-2.5 text-sm">
              {def.features.map((f, i) => (
                <li key={f} className="flex items-start gap-2.5 opacity-0 animate-[fadeUp_.5s_ease-out_forwards]" style={{ animationDelay: `${120 + i * 90}ms` }}>
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/25">
                    <Check className="h-3 w-3" strokeWidth={3.5} />
                  </span>
                  <span className="text-foreground/90">{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-2xl border border-border/60 bg-background/50 p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Due today</span>
                <span className="tabular-nums font-semibold text-lg text-primary">${def.price.toFixed(2)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-primary" /> 30-day money back</span>
                <span>Renews monthly</span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-1.5 text-[10px] text-muted-foreground">
              <span className="inline-flex items-center justify-center gap-1 rounded-lg border border-border/60 bg-card/60 px-2 py-1.5">
                <ShieldCheck className="h-3 w-3 text-primary" /> Stripe
              </span>
              <span className="inline-flex items-center justify-center gap-1 rounded-lg border border-border/60 bg-card/60 px-2 py-1.5">
                <Lock className="h-3 w-3 text-primary" /> 256-bit
              </span>
              <span className="inline-flex items-center justify-center gap-1 rounded-lg border border-border/60 bg-card/60 px-2 py-1.5">
                <Check className="h-3 w-3 text-primary" /> No fees
              </span>
            </div>

            <div className="mt-5 flex items-center gap-3 rounded-xl border border-border/50 bg-background/40 p-3">
              <div className="flex -space-x-2">
                <div className="h-6 w-6 rounded-full ring-2 ring-card bg-gradient-to-br from-primary to-primary/40" />
                <div className="h-6 w-6 rounded-full ring-2 ring-card bg-gradient-to-br from-[#7c3aed] to-primary/50" />
                <div className="h-6 w-6 rounded-full ring-2 ring-card bg-gradient-to-br from-emerald-400 to-primary/60" />
              </div>
              <p className="text-[11px] leading-tight text-muted-foreground">
                Joined by <span className="text-foreground font-medium">1,200+ freelancers</span> shipping paid work this month.
              </p>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

const PAID_AMOUNT_CENTS: Record<PaidPlanId, number> = {
  starter: 999,
  pro: 1999,
  unlimited: 3499,
};

function PaidSignUpForm({ plan, initialEmail }: { plan: PaidPlanId; initialEmail?: string }) {
  const [stripePromise, setStripePromise] = useState<Promise<StripeJs | null> | null>(null);
  const [initErr, setInitErr] = useState<string | null>(null);
  const getKey = useServerFn(getStripePublishableKey);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { publishableKey } = await getKey();
        if (cancelled) return;
        setStripePromise(loadStripe(publishableKey));
      } catch (e) {
        if (!cancelled) setInitErr(e instanceof Error ? e.message : "Failed to load Stripe");
      }
    })();
    return () => { cancelled = true; };
  }, [getKey]);

  const amount = PAID_AMOUNT_CENTS[plan];

  if (initErr) {
    return (
      <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
        Couldn't load secure checkout: {initErr}
      </div>
    );
  }
  if (!stripePromise) {
    return (
      <div className="mt-4 space-y-3">
        <div className="h-11 w-full animate-pulse rounded-lg bg-muted/60" />
        <div className="h-11 w-full animate-pulse rounded-lg bg-muted/60" />
        <div className="h-11 w-2/3 animate-pulse rounded-lg bg-muted/60" />
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparing secure checkout…
        </div>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        mode: "subscription",
        amount,
        currency: "usd",
        paymentMethodCreation: "manual",
        appearance: {
          theme: "night",
          variables: {
            colorPrimary: "#7c3aed",
            colorBackground: "#0b0b12",
            colorText: "#e5e7eb",
            colorTextSecondary: "#9ca3af",
            colorDanger: "#f87171",
            fontFamily: "Inter, system-ui, sans-serif",
            borderRadius: "12px",
            spacingUnit: "4px",
          },
          rules: {
            ".Input": {
              backgroundColor: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "none",
            },
            ".Input:focus": {
              border: "1px solid #7c3aed",
              boxShadow: "0 0 0 3px rgba(124,58,237,0.25)",
            },
            ".Tab, .Block": {
              backgroundColor: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
            },
            ".Tab--selected": { borderColor: "#7c3aed" },
            ".Label": { color: "#cbd5e1" },
          },
        },
      }}
    >
      <PaidSignUpInner plan={plan} initialEmail={initialEmail} />
    </Elements>
  );
}

function PaidSignUpInner({ plan, initialEmail }: { plan: PaidPlanId; initialEmail?: string }) {
  const { t, i18n } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const createSub = useServerFn(createSubscription);

  const [email, setEmail] = useState(initialEmail ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [legalOpen, setLegalOpen] = useState<"terms" | "privacy" | null>(null);

  const [busy, setBusy] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState<string | null>(null);
  const [existingEmail, setExistingEmail] = useState<string | null>(null);
  const [method, setMethod] = useState<"card" | "paypal" | "baridimob">("card");
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);
  const [baridiSubmitted, setBaridiSubmitted] = useState(false);
  const captchaRef = useRef<TurnstileGateHandle>(null);

  const score = useMemo(() => passwordScore(password), [password]);
  const allRulesMet = score === RULES.length;
  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = allRulesMet && passwordsMatch && accepted && !!fullName && !!email && (method === "baridimob" || method === "paypal" || (!!stripe && !!elements));

  async function createAccount(): Promise<{ userId: string; hasSession: boolean } | null> {
    const captchaToken = (await captchaRef.current?.ensureToken()) ?? undefined;
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
      toast.error(captchaRequiredMessage(t));
      return null;
    }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: { full_name: fullName, role: "freelancer" },
        captchaToken,
      },
    });
    captchaRef.current?.reset();
    if (error) { toast.error(error.message); return null; }
    if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
      setExistingEmail(email);
      return null;
    }
    return { userId: data.user!.id, hasSession: !!data.session };
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allRulesMet) return toast.error(t("auth.passwordNotMet"));
    if (!passwordsMatch) return toast.error(t("auth.passwordMismatch"));
    if (!accepted) return toast.error(t("auth.acceptTermsError"));

    // BaridiMob / PayPal branch: create account first, then show the payment step inline.
    if (method === "baridimob" || method === "paypal") {
      setBusy(true);
      const res = await createAccount();
      setBusy(false);
      if (!res) return;
      if (!res.hasSession) {
        // Save intent so we can nudge them post-confirmation.
        try { window.localStorage.setItem("pending_plan", plan); } catch {}
        setNeedsEmailConfirm(email);
        return;
      }
      setCreatedUserId(res.userId);
      return;
    }

    if (!stripe || !elements) return;

    setBusy(true);
    setPayError(null);

    // 1. Validate PaymentElement inputs before touching the account.
    const { error: submitErr } = await elements.submit();
    if (submitErr) {
      setBusy(false);
      setPayError(submitErr.message ?? "Please check your card details.");
      return;
    }

    // 2. Create the freelancer account.
    const captchaToken = (await captchaRef.current?.ensureToken()) ?? undefined;
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
      setBusy(false);
      toast.error(captchaRequiredMessage(t));
      return;
    }
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: { full_name: fullName, role: "freelancer" },
        captchaToken,
      },
    });
    captchaRef.current?.reset();
    if (error) {
      setBusy(false);
      toast.error(error.message);
      return;
    }
    if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
      setBusy(false);
      setExistingEmail(email);
      return;
    }
    // If no session (email confirmation required), we can't create the
    // subscription right now — keep the plan and resume after they confirm.
    if (!data.session) {
      try { window.localStorage.setItem("pending_plan", plan); } catch {}
      setBusy(false);
      setNeedsEmailConfirm(email);
      return;
    }

    // 3. Create the subscription (server-side, freelancer-enforced).
    try {
      const res = await createSub({ data: { plan } });
      if (!("clientSecret" in res) || !res.clientSecret) {
        // Already active on this plan.
        setSucceeded(true);
        setTimeout(() => { window.location.replace("/dashboard"); }, 1400);
        return;
      }

      // 4. Confirm the payment with PaymentElement, staying on lumvio.co.
      const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: res.clientSecret,
        confirmParams: { return_url: `${window.location.origin}/dashboard` },
        redirect: "if_required",
      });
      if (confirmErr) {
        setBusy(false);
        setPayError(confirmErr.message ?? "Payment failed. Please try again.");
        return;
      }
      if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        setSucceeded(true);
        toast.success(`Welcome to Lumvio ${plan[0].toUpperCase() + plan.slice(1)}!`);
        setTimeout(() => { window.location.replace("/dashboard"); }, 1400);
        return;
      }
      setBusy(false);
    } catch (err) {
      setBusy(false);
      setPayError(err instanceof Error ? err.message : "Could not start subscription.");
    }
  }

  if (succeeded) {
    return (
      <div className="mt-6 flex flex-col items-center justify-center py-12 text-center">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-emerald-500/25 blur-2xl" />
          <CheckCircle2 className="relative h-16 w-16 text-emerald-500 animate-[pulse_1.5s_ease-in-out_infinite]" />
        </div>
        <div className="mt-4 text-lg font-semibold">Account created & subscribed</div>
        <div className="mt-1 text-sm text-muted-foreground">Taking you to your dashboard…</div>
      </div>
    );
  }

  if (needsEmailConfirm) {
    return (
      <div className="mt-6 space-y-4">
        <div className="rounded-xl border border-primary/30 bg-primary/10 p-5">
          <div className="text-sm font-semibold text-foreground">Confirm your email to finish subscribing</div>
          <p className="mt-1 text-sm text-muted-foreground">
            We sent a confirmation link to <span className="text-foreground font-medium">{needsEmailConfirm}</span>.
            Once you confirm and sign in, we'll take you straight to secure checkout to complete your <span className="text-foreground font-medium">{plan}</span> subscription.
          </p>
        </div>
        <Button asChild variant="outline" className="w-full">
          <Link to="/auth" search={{ tab: "signin" }}>Go to sign in</Link>
        </Button>
      </div>
    );
  }

  if (createdUserId && method === "baridimob") {
    return (
      <div className="mt-6 space-y-4">
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            Account created — one last step
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Complete your BaridiMob transfer and upload the proof. We'll activate your <span className="text-foreground font-medium">{plan}</span> plan within 24h.
          </p>
        </div>
        <BaridiMobPaymentCard
          plan={plan}
          amountCents={PAID_AMOUNT_CENTS[plan]}
          userId={createdUserId}
          defaultSenderName={fullName}
          onSubmitted={() => {
            setBaridiSubmitted(true);
            setTimeout(() => { window.location.replace("/dashboard"); }, 2200);
          }}
        />
        {baridiSubmitted && (
          <p className="text-center text-xs text-muted-foreground">Redirecting to your dashboard…</p>
        )}
      </div>
    );
  }

  if (createdUserId && method === "paypal") {
    return (
      <div className="mt-6 space-y-4">
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            Account created — one last step
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Complete your <span className="text-foreground font-medium">{plan}</span> subscription with PayPal below.
          </p>
        </div>
        <PayPalSubscribeButton
          plan={plan}
          onSuccess={() => { setTimeout(() => window.location.replace("/dashboard"), 800); }}
        />
      </div>
    );
  }



  return (
    <>
      {existingEmail && (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
          <p className="font-medium text-foreground">{t("auth.emailExists")}</p>
          <p className="mt-1 text-muted-foreground">
            {existingEmail}{" "}
            <Link
              to="/auth"
              search={{ tab: "signin", plan }}
              className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
            >
              {t("auth.goSignIn")}
            </Link>
          </p>
        </div>
      )}
      <form onSubmit={onSubmit} className="mt-4 space-y-4">
        {/* Account fields */}
        <div className="space-y-1.5">
          <Label htmlFor="psu-name">{t("common.fullName")}</Label>
          <Input id="psu-name" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="psu-email">{t("common.email")}</Label>
          <Input
            id="psu-email"
            type="email"
            required
            dir="ltr"
            value={email}
            placeholder="you@example.com"
            onChange={(e) => { setEmail(e.target.value); if (existingEmail) setExistingEmail(null); }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="psu-password">{t("common.password")}</Label>
          <div className="relative" dir="ltr">
            <Input
              id="psu-password"
              type={showPw ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={"pe-10"}
            />
            <button
              type="button"
              aria-label={showPw ? t("auth.hidePassword") : t("auth.showPassword")}
              onClick={() => setShowPw((v) => !v)}
              className={`absolute top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground right-2`}
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {password.length > 0 && <StrengthBar score={score} />}
          <ul className="mt-1 grid grid-cols-1 gap-0.5 text-[11px]">
            {RULES.map((r) => {
              const ok = r.test(password);
              return (
                <li key={r.key} className={`flex items-center gap-1.5 ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                  {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  {t(r.labelKey)}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="psu-confirm">{t("auth.confirmPassword")}</Label>
          <div className="relative" dir="ltr">
            <Input
              id="psu-confirm"
              type={showConfirm ? "text" : "password"}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              className={`pe-10 ${
                confirm.length === 0 ? "" : passwordsMatch ? "border-emerald-500/60" : "border-destructive/60"
              }`}
            />
            <button
              type="button"
              aria-label={showConfirm ? t("auth.hidePassword") : t("auth.showPassword")}
              onClick={() => setShowConfirm((v) => !v)}
              className={`absolute top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground right-2`}
            >
              {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {confirm.length > 0 && !passwordsMatch && (
            <p className="text-[11px] text-destructive">{t("auth.passwordMismatch")}</p>
          )}
        </div>

        {/* Freelancer-only note */}
        <div className="rounded-lg border border-primary/40 bg-primary/10 p-3 text-xs">
          <div className="font-medium text-foreground">
            {t("auth.freelancer")}
            <span className="ms-1.5 rounded-full bg-primary/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-primary">
              {plan}
            </span>
          </div>
          <div className="mt-0.5 text-muted-foreground">
            Paid plans are for freelancer accounts. Client accounts are always free.
          </div>
        </div>

        {/* Terms */}
        <label className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3 text-xs">
          <Checkbox
            checked={accepted}
            onCheckedChange={(v) => setAccepted(v === true)}
            className="mt-0.5"
            id="psu-terms"
          />
          <span className="text-muted-foreground">
            {t("auth.termsPrefix")}{" "}
            <button type="button" onClick={(e) => { e.preventDefault(); setLegalOpen("terms"); }} className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
              {t("auth.terms")}
            </button>{" "}
            {t("auth.and")}{" "}
            <button type="button" onClick={(e) => { e.preventDefault(); setLegalOpen("privacy"); }} className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
              {t("auth.privacy")}
            </button>.
          </span>
        </label>
        <LegalDialog open={legalOpen !== null} onOpenChange={(o) => !o && setLegalOpen(null)} kind={legalOpen ?? "terms"} />

        {/* Payment section */}
        <div className="rounded-2xl border border-border/60 bg-background/40 p-4 sm:p-5">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <CreditCard className="h-4 w-4 text-primary" /> Payment method
            </div>
          </div>

          <div className="mb-4 grid grid-cols-3 gap-2 rounded-xl border bg-background/50 p-1">
            <button
              type="button"
              onClick={() => setMethod("card")}
              className={`flex items-center justify-center gap-2 rounded-lg py-2 text-xs sm:text-sm font-medium transition-colors ${
                method === "card"
                  ? "bg-primary/15 text-foreground ring-1 ring-primary/30"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <CreditCard className="h-4 w-4" /> Card
            </button>
            <button
              type="button"
              onClick={() => setMethod("paypal")}
              className={`flex items-center justify-center gap-2 rounded-lg py-2 text-xs sm:text-sm font-medium transition-colors ${
                method === "paypal"
                  ? "bg-[#003087]/10 text-[#003087] ring-1 ring-[#003087]/30 dark:bg-[#0070ba]/15 dark:text-[#e6f0ff] dark:ring-[#0070ba]/30"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden><path d="M7.5 21.5H4l2.5-15h5.75c3 0 4.9 1.65 4.4 4.6-.55 3.3-3 5.15-6.4 5.15H8.6L7.5 21.5Zm2-8.5h1.35c1.75 0 2.9-.85 3.15-2.45.2-1.25-.55-1.95-2-1.95H10.3L9.5 13Z"/><path opacity=".6" d="M17.5 8.5c.35 3.1-1.95 5.5-5.6 5.5h-1.35l-1.1 7h3.5l.6-3.75h1.85c3.15 0 5.35-1.7 5.85-4.75.35-2.15-.7-3.65-3-4Z"/></svg>
              PayPal
            </button>
            <button
              type="button"
              onClick={() => setMethod("baridimob")}
              className={`flex items-center justify-center gap-2 rounded-lg py-2 text-xs sm:text-sm font-medium transition-colors ${
                method === "baridimob"
                  ? "bg-yellow-400/15 text-blue-900 ring-1 ring-yellow-400/30 dark:text-yellow-100"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <img src={baridimobLogo.url} alt="" className="h-5 w-5 rounded-sm bg-white p-[1px]" /> BaridiMob
            </button>
          </div>

          {method === "card" && (
            <>
              <PaymentElement
                options={{
                  layout: "tabs",
                  terms: {
                    card: "never",
                    applePay: "never",
                    googlePay: "never",
                    cashapp: "never",
                    usBankAccount: "never",
                    auBecsDebit: "never",
                    bancontact: "never",
                    ideal: "never",
                    sepaDebit: "never",
                    sofort: "never",
                    paypal: "never",
                  },
                }}
              />
              {payError && (
                <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-400">
                  {payError}
                </div>
              )}

              {/* Branded authorization / mandate block */}
              <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.07] bg-gradient-to-b from-white/[0.04] to-transparent">
                <div className="flex items-start gap-3 p-3.5">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/12 text-primary ring-1 ring-primary/25">
                    <ShieldCheck className="h-4 w-4" />
                  </span>
                  <p className="text-[11px] leading-relaxed text-muted-foreground">
                    You authorize <span className="font-medium text-foreground">Lumvio</span> to charge you{" "}
                    <span className="font-medium text-foreground">
                      ${(PAID_AMOUNT_CENTS[plan] / 100).toFixed(2)}/month
                    </span>{" "}
                    on a recurring basis until you cancel. Cancel anytime from Billing — no questions asked.
                  </p>
                </div>
                <div className="flex items-center justify-center gap-1.5 border-t border-white/[0.06] bg-white/[0.02] px-3 py-2 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  <Lock className="h-3 w-3" /> Encrypted by Stripe · PCI-DSS Level 1
                </div>
              </div>
            </>
          )}


          {method === "paypal" && (
            <div className="rounded-lg border border-[#0070ba]/30 bg-[#0070ba]/5 p-3 text-xs text-muted-foreground">
              You'll create your account first, then complete your <span className="text-foreground font-medium">{plan}</span> subscription with PayPal.
            </div>
          )}

          {method === "baridimob" && (
            <div className="rounded-lg border border-yellow-400/20 bg-yellow-400/5 p-3 text-xs text-blue-900 dark:text-yellow-100/80">
              You'll create your account first, then upload your BaridiMob transfer screenshot.
              Our team activates your plan within <span className="text-foreground font-medium">24 hours</span> after review.
            </div>
          )}
        </div>

        <TurnstileGate ref={captchaRef} action="signup-checkout" />


        <Button
          type="submit"
          className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-[#7c3aed] text-primary-foreground hover:opacity-95 shadow-[0_16px_40px_-14px_color-mix(in_oklab,var(--primary)_75%,transparent)] font-medium"
          disabled={busy || !canSubmit}
        >
          {busy ? (
            <><Loader2 className="me-2 h-4 w-4 animate-spin" /> Processing…</>
          ) : method === "baridimob" ? (
            <>Create account · Pay ${(PAID_AMOUNT_CENTS[plan] / 100).toFixed(2)} via BaridiMob</>
          ) : method === "paypal" ? (
            <>Create account · Continue with PayPal</>
          ) : (
            <>Create account & Subscribe · ${(PAID_AMOUNT_CENTS[plan] / 100).toFixed(2)}/mo</>
          )}
        </Button>
        <p className="text-center text-[11px] text-muted-foreground">
          By subscribing you agree to Lumvio's Terms. You can cancel anytime from Billing.
        </p>
      </form>
    </>
  );
}
